import { useEffect, useState } from "react";

const DEFER_DELAY_MS = 5000;

/**
 * Returns true after 5s, useful for deferring low-priority queries
 * so they don't compete with critical page data on initial load.
 *
 * All callers share a single timer — multiple useDeferredReady() calls
 * won't create redundant timeouts. The ready state persists for the
 * lifetime of the browser tab (not reset on SPA navigation or re-login).
 */

let globalReady = false;
const subscribers = new Set<(ready: boolean) => void>();
let timerId: ReturnType<typeof setTimeout> | null = null;

function startTimer() {
  if (timerId || globalReady) return;
  timerId = setTimeout(() => {
    globalReady = true;
    timerId = null;
    subscribers.forEach((cb) => cb(true));
  }, DEFER_DELAY_MS);
}

export default function useDeferredReady(): boolean {
  const [ready, setReady] = useState(globalReady);

  useEffect(() => {
    if (globalReady) {
      setReady(true);
      return;
    }
    subscribers.add(setReady);
    startTimer();
    return () => {
      subscribers.delete(setReady);
    };
  }, []);

  return ready;
}

/** Reset module state — for testing only. */
export function __resetForTesting() {
  globalReady = false;
  subscribers.clear();
  if (timerId) clearTimeout(timerId);
  timerId = null;
}
